import { api } from './api';

/**
 * Conflict review API client.
 *
 * Types are transcribed from ACTUAL captured responses, not from the Prisma models —
 * the wire shape is what the UI has to survive.
 *
 * The frontend deliberately holds NO conflict, regulatory, authority or lifecycle rules.
 * `availableConsequences` / `blockedConsequences` and `mandatoryCessation` are computed
 * server-side and rendered here; re-deriving rC21 or the silk/admin override in React
 * would be a second copy of a regulatory rule, and two copies drift.
 */

export type ConflictReviewStatus = 'pending' | 'decided' | 'withdrawn';

export type ConflictRegulatoryBasis =
  | 'rC21_professional_embarrassment'
  | 'rC21_confidential_information'
  | 'former_client_informed_consent'
  | 'information_barrier_sufficient'
  | 'no_conflict_on_review'
  | 'other';

export type ConflictConsequence = 'continue_acting' | 'cease' | 'reassign';

/**
 * A conflict this review is about.
 *
 * REDACTED server-side for a barrister caller (a silk can reach this queue): the other
 * member's matter identity is withheld and `reason` becomes a generic chambers-review
 * message, while `severity` survives so the review still reads as serious.
 */
export interface ReviewConflict {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  matchMethod: string;
  reason: string;
  status: string;
  existingMatterId: string | null;
  existingMatterName: string | null;
  existingMatterNumber: string | null;
  existingBarrister: string | null;
  detectedAt: string;
}

/** Counsel currently on the engagement under review. */
export interface ReviewCounsel {
  allocationId: string;
  barristerId: string;
  name: string;
  isSilk: boolean;
  role: 'lead' | 'junior' | 'second_counsel' | 'specialist';
  allocationStatus: string;
}

export interface ConflictReview {
  id: string;
  caseId: string;
  instructionId: string;
  severity: string;
  status: ConflictReviewStatus;

  regulatoryBasis: ConflictRegulatoryBasis | null;
  basisNotes: string | null;
  /** Server-rendered phrasing, e.g. "professional embarrassment (rC21 — cessation mandatory)". */
  basisLabel: string | null;
  /** null until the basis is assessed. */
  mandatoryCessation: boolean | null;
  assessedAt: string | null;
  assessedBy: { id: string; firstName: string; lastName: string } | null;

  consequence: ConflictConsequence | null;
  decisionReason: string | null;
  decidedAt: string | null;
  decidedBy: { id: string; firstName: string; lastName: string } | null;

  ceasedAllocationId: string | null;
  replacementAllocationId: string | null;
  createdAt: string;

  case: { id: string; caseNumber: string; caseName: string; conflictStatus: string };
  instruction: { id: string; status: string };

  /** The conflicts that caused this review. Empty only on pre-existing rows. */
  conflicts: ReviewConflict[];
  counsel: ReviewCounsel[];
  /** What THIS user may choose right now. Rendered, never derived. */
  availableConsequences: ConflictConsequence[];
  blockedConsequences: { consequence: ConflictConsequence; reason: string }[];
}

export async function listConflictReviews(status?: 'pending' | 'decided'): Promise<ConflictReview[]> {
  const { data } = await api.get('/conflicts/reviews', {
    headers: { 'Cache-Control': 'no-store' },
    params: status ? { status } : {},
  });
  return data.data;
}

export async function assessRegulatoryBasis(
  reviewId: string,
  payload: { regulatoryBasis: ConflictRegulatoryBasis; basisNotes?: string },
): Promise<ConflictReview & { mandatoryCessation: boolean }> {
  const { data } = await api.post(`/conflicts/reviews/${reviewId}/assess`, payload);
  return data.data;
}

export interface DecideResult {
  review: { id: string; status: string; consequence: ConflictConsequence };
  /**
   * On cease/reassign the API hands back the allocation to return. The decision
   * AUTHORISES the return; the existing return lifecycle performs it, which is where the
   * rC27 explanation/consent gate lives. The UI must not shortcut that.
   */
  allocationToReturn: string | null;
  mandatoryCessation: boolean;
  nextStep: string | null;
}

export async function decideConflictReview(
  reviewId: string,
  payload: { consequence: ConflictConsequence; reason: string },
): Promise<DecideResult> {
  const { data } = await api.post(`/conflicts/reviews/${reviewId}/decide`, payload);
  return data.data;
}

// ─── Display helpers ────────────────────────────────────────

export const BASIS_OPTIONS: { value: ConflictRegulatoryBasis; label: string }[] = [
  { value: 'rC21_professional_embarrassment', label: 'Professional embarrassment (rC21)' },
  { value: 'rC21_confidential_information', label: 'Confidential information of another client (rC21)' },
  { value: 'former_client_informed_consent', label: 'Former client — informed consent given' },
  { value: 'information_barrier_sufficient', label: 'Managed by an information barrier' },
  { value: 'no_conflict_on_review', label: 'Not a real conflict on review' },
  { value: 'other', label: 'Other' },
];

export const CONSEQUENCE_LABEL: Record<ConflictConsequence, string> = {
  continue_acting: 'Continue acting',
  cease: 'Cease',
  reassign: 'Cease and reassign',
};

export const ROLE_LABEL: Record<ReviewCounsel['role'], string> = {
  lead: 'Lead',
  junior: 'Junior',
  second_counsel: 'Second counsel',
  specialist: 'Specialist',
};

/** How the conflict was found. The raw value means nothing to a clerk. */
export const MATCH_METHOD_LABEL: Record<string, string> = {
  party_name: 'Party name match',
  contact_id: 'Same contact record',
  solicitor_firm: 'Same instructing firm',
  barrister_conflict: 'Counsel already acting',
  phone_match: 'Same phone number',
  clerk_conflict: 'Clerk supports both sides',
  former_client: 'Former client',
  chambers_wide: 'Another member of chambers',
  shared_contact_role: 'Contact in contradictory roles',
};
